/**
 * The company-settings slice of the wire — Task CS-2, consuming
 * `GET`/`PUT /api/v1/company/settings` (API_CONTRACT §8.14, `openapi.yaml`'s
 * `getCompanySettings` / `updateCompanySettings` and the `CompanySettings` /
 * `CompanySettingsUpdateRequest` schemas).
 *
 * Hand-written to mirror the contract, the same discipline `retention.ts` and
 * `permission-grants.ts` state: transcribe what the screen reads, not the whole
 * document.
 *
 * ── The two doors are not the same door ─────────────────────────────────────
 *
 * `GET` is `workforce.view_self` at scope `self`: **everybody needs the currency
 * code to read their own payslip**, and there is nothing on this surface to
 * withhold. `PUT` is `workforce.manage_policies` at scope `company`. That is why
 * `useCompanyCurrency` below is safe to call from an employee's own payslip panel
 * while the screen that writes these values is behind a manager's menu gate.
 *
 * ── Two of the five fields are read-only here ───────────────────────────────
 *
 * `timezone` and `locale` are written by the achau mirror, not by this surface, and
 * the `PUT` body is `.strict()` — sending either is a `400`. They are on the
 * response because the screen shows them, never because it may change them.
 *
 * ── What these two codes do NOT change ──────────────────────────────────────
 *
 * The payroll engine is Vietnamese statute — the progressive PIT tariff, the
 * BHXH/BHYT/BHTN caps — and Task CS-1 left it untouched to the line. There is no
 * exchange rate anywhere in the service and stored figures are never restated.
 * `country_code` and `currency_code` decide how money is **labelled** and which
 * country's rules the AI is instructed to follow. The screen says this in words; it
 * is repeated here so no reader meets the type without meeting its ceiling.
 */

import { useApiQuery, type QueryResult } from './useApiQuery';

export const COMPANY_SETTINGS_PATH = '/api/v1/company/settings';

/**
 * `CompanySettings` of `openapi.yaml` — `additionalProperties: false`, and nothing
 * nullable: every one of the five fields is `NOT NULL` in the schema, so each is
 * written out plainly rather than as `T | null`.
 */
export interface CompanySettings {
  readonly company_id: string;
  /** ISO 3166-1 alpha-2, upper case. `VN` until somebody changes it. */
  readonly country_code: string;
  /** ISO 4217, upper case. `VND` until somebody changes it. */
  readonly currency_code: string;
  /** Read-only on this surface — the achau mirror writes it. */
  readonly timezone: string;
  /** Read-only on this surface — the achau mirror writes it. */
  readonly locale: string;
  readonly updated_at: string;
}

/** `CompanySettingsUpdateRequest` — both codes required, and nothing else accepted. */
export interface CompanySettingsUpdateRequest {
  readonly country_code: string;
  readonly currency_code: string;
}

/**
 * What every screen that prints money falls back to while the read is in flight, or
 * when it failed.
 *
 * `VND` and not an empty label: migration 067's column default is `'VND'`, so it is
 * the answer for every company that has never opened the settings screen, and a
 * blank unit beside a figure is worse for the reader than the overwhelmingly likely
 * one. The figure itself is never withheld — that would be rendering a transport
 * failure as missing pay.
 */
export const FALLBACK_CURRENCY = 'VND';

/** The company's country and currency. One `GET`, three outcomes, as everywhere. */
export function useCompanySettings(): QueryResult<CompanySettings> {
  return useApiQuery<CompanySettings>(COMPANY_SETTINGS_PATH);
}

/**
 * Just the currency code, for the screens that only need to label a number.
 *
 * No cache between mounts. `useApiQuery` re-reads on mount, so a screen with three
 * money panels makes three small reads of a six-field row — and a module-level cache
 * would be a second copy of the company's configuration that nothing invalidates
 * when somebody changes it on `/settings` in another tab. If this ever shows up in a
 * profile, the honest fix is to hang one read on the session provider, not to
 * remember an answer here.
 */
export function useCompanyCurrency(): string {
  return useCompanySettings().data?.currency_code ?? FALLBACK_CURRENCY;
}
